/**
 * Guardas de rota.
 *
 * Escondem tela, não protegem dado — a API resolve o workspace pela SESSÃO e
 * responde 403/404 por conta própria (invariante 1). O que estas guardas fazem
 * é não mostrar uma tela quebrada e não perder o destino do usuário.
 */
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Location } from 'react-router';
import { Skeleton } from '@mgdlead/ui';

import { useSession } from './useSession';

export const NEXT_PARAM = 'next';

/**
 * Monta `/login?next=<destino>`.
 *
 * O destino vai na QUERY, não no state do router: state morre no refresh e na
 * aba nova, e "abri o link do incidente, fui pro login e voltei pra home" é
 * exatamente o tipo de coisa que faz o usuário desistir do link.
 */
export function buildLoginRedirect(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
): string {
  const destination = `${location.pathname}${location.search}${location.hash}`;
  if (destination === '/' || destination.startsWith('/login')) return '/login';
  return `/login?${NEXT_PARAM}=${encodeURIComponent(destination)}`;
}

/**
 * Lê o destino guardado. Descarta URL absoluta: `next=https://outro.site`
 * transformaria o login numa máquina de phishing (open redirect).
 */
export function readNextPath(search: string): string | null {
  const raw = new URLSearchParams(search).get(NEXT_PARAM);
  if (raw === null || raw.length === 0) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

/** Espera do carregamento da sessão: esqueleto, nunca spinner de tela cheia. */
export function RouteLoading() {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="min-h-dvh bg-background p-4">
      <span className="sr-only">Carregando</span>
      <div className="mx-auto flex w-full max-w-md flex-col gap-3 pt-16">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

/** Sem sessão -> login, guardando o destino. */
export function RequireAuth() {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteLoading />;
  if (status === 'unauthenticated') return <Navigate to={buildLoginRedirect(location)} replace />;

  return <Outlet />;
}

/** Já logado não vê tela de login/cadastro: vai para onde queria ir. */
export function RequireGuest() {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <RouteLoading />;
  if (status === 'authenticated')
    return <Navigate to={readNextPath(location.search) ?? '/'} replace />;

  return <Outlet />;
}
